/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-react';
import posed, { PoseGroup } from 'react-pose';
import { Link, Redirect, withRouter } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Progress from 'components/Progress';
import SweetAlert from 'components/SweetAlert';
import TrainingContent, { LinkButton, NavigationButton } from 'components/TrainingContent';

import APIHandler from 'config/APIHandler';

import { useChapters } from 'hooks/useChapters';
import { useProgressHandler } from 'hooks/useProgressHandler';
import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

import 'assets/styles/sweetalert.css';

import { buttonRules, fastTrackId } from 'settings';

// TODO: Rip out the fasttrack logic (currently unused in this version of the template) and if required think of alternative solution (separate component for fasttrack, similar to retain?)

const AnimationContainer = posed.section({
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 200,
    },
  },
  enter: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 200,
    },
  },
});

// Interaction with the fastTrackData in localStorage. if ONLY the key is passed, it will return the value. If both key and value are passed, it will set the value. If no key is passed, it will throw an error.
const fastTrackData = (key, value = null) => {
  const data = JSON.parse(localStorage.getItem('fastTrackData')) || {};
  if (key && value === null) {
    if (data) return data[key];
    return null;
  }
  if (key && value) {
    data[key] = value;
    return localStorage.setItem('fastTrackData', JSON.stringify(data));
  }
  throw Error('Invalid input');
};

/**
 * @typedef {object} TrainingContentViewProps
 * @property {string} type - The type of the training content. Can be one of ['training', 'retain', 'fasttrack']
 * @property {object} match - The match object from the router
 * @property {object} history - The history object from the router
 * @property {string} redirPath - The path to redirect to
 * @property {boolean} locked - Indicates if the training is locked
 * @property {boolean} comingSoon - Indicates if the training is coming soon
 */

/**
 * View process:
 *
 * - Needs to load page data from the api (information about the training, independant of the user)
 * - Needs to load the user's current progress in the training to determine access, render progress meter, etc.
 * - Needs to register the training start date if it has not been registered yet, using progress data.
 *
 * @param {TrainingContentViewProps} props Props for the trainingView component, passed by router
 * @returns {JSX.Element} The training content view
 */
const TrainingContentView = ({ type = 'training', match, history }) => {
  const { pageId, chapterId } = match.params;

  const [completedBefore, setCompletedBefore] = useState(null);

  const [chapterPage, setChapterPage] = useState(null);
  const [pages, setPages] = useState([]);
  const [sweetAlertTitle, setSweetAlertTitle] = useState(null);
  const [errors, setErrors] = useState(0);
  const [contentCompleted, setContentCompleted] = useState(type === 'retain');

  const [pageNotCompleted, setPageNotCompleted] = useState(false);

  // States to controle navigationButtons settings
  const [buttons, setButtons] = useState([]);
  const [buttonsCompleted, setButtonsCompleted] = useState(0);
  const [buttonContentCompleted, setButtonContentCompleted] = useState(['retain', 'fasttrack'].includes(type));
  const [timerCompleted, setTimerCompleted] = useState(['retain', 'fasttrack'].includes(type));

  // Hooks for loading the necessary data
  const { appId, loaded: loadedSettings, lang } = useSettings();
  const { user, updateUser, loaded: loadedUser } = useUserInfo();
  const { chapters, loaded: loadedChapters, categories } = useChapters();

  const { langFile, code } = lang;

  // pageId in url is index 0 based, while database is index 1 based. Hook expects database-accurate page id
  const pageCategory = categories.find((category) => {
    const { chapterIDs } = category;
    return chapterIDs.includes(Number(chapterId));
  });
  const { openableSection = false, color } = pageCategory || {};
  const {
    loaded: loadedProgressHandler,
    isAccessible,
    target,
    next: nextPageUrl,
    previous: previousPageUrl,
    update: progressUpdate,
  } = useProgressHandler(Number(chapterId), Number(pageId) + 1, type, openableSection ? 'extra' : '');

  // technically not required, but consolidates various individual states for easier access
  const loadedPrerequisites = loadedSettings && loadedUser && loadedChapters && loadedProgressHandler;
  const contentIncomplete = !contentCompleted || !buttonContentCompleted || !timerCompleted;

  /** Memo to filter for only the relevant chapter data for the current page. */
  const { pageChapter, loadedPageChapter } = useMemo(() => {
    if (loadedChapters) {
      const chapter = chapters.find((checkedChapter) => Number(checkedChapter.id) === Number(chapterId));
      return {
        pageChapter: chapter,
        loadedPageChapter: true,
      };
    }
    return {
      pageChapter: {},
      loadedPageChapter: false,
    };
  }, [chapterId, chapters, loadedChapters]);

  const { percentage: chapterPercentage = 0, chapterItemCount, alternativeTrain } = pageChapter;

  // Memo to determine the return button url. Defaults to /main/training in most cases, except when the pageCategory is an openableSection. Optional: Adapt to include the trainingType in the url instead of training.
  const returnBtnUrl = useMemo(() => {
    const defaultUrl = '/main/training';
    if (!loadedChapters) return defaultUrl;

    const relevantPageCategory = categories.find((category) => {
      const { chapterIDs } = category;
      return chapterIDs.includes(Number(chapterId));
    });
    const { openableSection: isExtraGroup = false, id: categoryId } = relevantPageCategory || {};

    if (isExtraGroup) return `/main/training/extra/${categoryId}`;

    return defaultUrl;
  }, [categories, chapterId, loadedChapters]);

  /** Check if the user has completed the current page before. Will only run if the pageChapter data is loaded. */
  useEffect(() => {
    const checkCompletion = () => {
      if (!loadedPageChapter) return;
      const { progress } = pageChapter;
      if (progress > Number(pageId)) setCompletedBefore(true);
    };
    checkCompletion();
  }, [loadedPageChapter, pageChapter, pageId]);

  /**
   * Training Registration useEffect: Will check if the user has done any training before, and if not, will register the
   * start date of the module. Will only attempt to register the start date if all prerequisite data is loaded from hooks.
   *
   * Has no further effect on the rest of the component.
   */
  useEffect(() => {
    const registerTraining = async () => {
      if (!loadedPrerequisites) return;
      const { training_started: trainingStarted } = user;

      if (trainingStarted) return;

      // for ensuring a leading 0 to ensure 2 digit format
      const leading = (val) => `0${val}`.slice(-2);

      const d = new Date(Date.now());
      const dateString = `${d.getFullYear()}-${leading(d.getMonth() + 1)}-${leading(d.getDate())}`;

      await updateUser(appId, [{ param: 'training_started', val: dateString }]);
    };

    registerTraining();
  }, [appId, user, updateUser, loadedPrerequisites]);

  /**
   * Load Page data. Will only do so if all prerequisite data from hooks is loaded. If the type is fasttrack, it will
   * fetch user-agnostic data from the API, based on the fastTrackId in settings. If the type is not fasttrack, it will
   * fetch the user specific data from the API, based on the chapterId in the url.
   */
  useEffect(() => {
    const loadPagesData = async () => {
      if (!loadedPrerequisites) return;

      // If the type is fasttrack, fetch the fasttrack data from the API and set it to the state
      if (type === 'fasttrack') {
        const pagesResponse = await APIHandler.getAppQuestions(fastTrackId);
        fastTrackData('totalQuestions', pagesResponse.length);
        setPages(pagesResponse);
        setChapterPage(pagesResponse[pageId]);
      } else {
        let fetchedPages = [];

        const pagesResponse = await APIHandler.getTrainingPages(chapterId, `${langFile}-${langFile.toUpperCase()}`);

        if (type === 'retain') {
          pagesResponse.forEach((page) => {
            if (page.type === undefined && page.pageType !== 'Final-Page') {
              fetchedPages.push(page);
            }
          });
        } else {
          fetchedPages = pagesResponse;
        }
        setPages(fetchedPages);
        setChapterPage(fetchedPages[pageId]);
      }
    };
    loadPagesData();
  }, [chapterId, loadedPrerequisites, pageId, type, lang, langFile, code]);

  /**
   * Handles the configuration of the navigation buttons settings, based on the current progress made and the current
   * page content Is dependant on both the general loadedPrerequisites and the specific chapterPage data being loaded.
   */
  useEffect(() => {
    if (!loadedPrerequisites || !chapterPage) return;

    const newButtons = [];
    let mandatoryAmount = 0;

    if (chapterPage) {
      // For certain pagetypes, there is a mandatory reading time of 15 seconds. This is to ensure the user has read the content.
      const mandatoryReadingTypes = ['infographic', 'photo', '', 'autoplay'];
      const { title, pageType } = chapterPage;
      if (title && mandatoryReadingTypes.includes(pageType.toLowerCase())) {
        setTimeout(() => {
          setTimerCompleted(true);
        }, 5000);
      } else {
        setTimerCompleted(true);
      }

      // Checks all linkType{number} params in the page data (using the number of keys in the object as a reference). If that linkType is referenced as mandatory in the buttonRules, it will increase the counter
      Object.keys(chapterPage).forEach((_, index) => {
        const linkTypeNumber = index + 1;
        const isMandatory = buttonRules[chapterPage[`linkType${linkTypeNumber}`]];
        if (isMandatory) {
          mandatoryAmount += 1;
        }
      });

      Object.keys(chapterPage).forEach((key) => {
        let linkNumber = 1;
        if (key.includes(`link${linkNumber}`)) {
          const mandatory = buttonRules[chapterPage[`linkType${linkNumber}`]];
          const links = [chapterPage[`link${linkNumber}`], chapterPage[`linkType${linkNumber}`]].every(
            (val) => val !== undefined && val.length > 0,
          );

          const linksValid = chapterPage.question === undefined && links;

          if (key === `link${linkNumber}` && linksValid) {
            newButtons.push(
              <LinkButton
                key={chapterPage[`linkType${linkNumber}`]}
                link={chapterPage[`link${linkNumber}`]}
                type={chapterPage[`linkType${linkNumber}`]}
                mandatory={mandatory}
                textHtml={chapterPage[`linkTextHtml${linkNumber}`]}
                completed={() => {
                  const newButtonAmount = buttonsCompleted + 1;
                  setButtonsCompleted((prev) => prev + 1);
                  if (newButtonAmount === mandatoryAmount) {
                    setButtonContentCompleted(true);
                  }
                }}
              />,
            );
          }

          linkNumber += 1;
        }
      });
      setButtons(newButtons);
    }

    if (mandatoryAmount === 0) setButtonContentCompleted(true);
  }, [buttonsCompleted, chapterPage, loadedPrerequisites]);

  const handleNextPage = useCallback(async () => {
    if (!loadedPageChapter) return;

    // since the state is updated a within this function, I want to be able to reference the original values of these state params.
    window.scrollTo(0, 0);

    // -------
    // prepatory state setting
    // -------

    // If the type is not fasttrack, and the content is incomplete (and not completed before), show a warning and return
    if (!['fasttrack'].includes(type)) {
      if ((!completedBefore && contentIncomplete) || (contentIncomplete && !completedBefore)) {
        const { question } = chapterPage;
        if (question) {
          return;
        } // stop this process if the page has a question, else continue
        setPageNotCompleted(true);
        setSweetAlertTitle(i18n.translate('trainingScreens.viewAll'));
        setErrors(2);
        return;
      }
    }

    // DataHandler checks for appId. Functionality has been taken over from useSettings.
    // DataHandler checks for key in storage. Functionality has been taken over from useChapters --> chapters obj. --> saved the current chapter under chapterPage.

    const { chapterItemCount: totalNumberOfPages, chapterNumber } = pageChapter;

    // 1) IF this is the second to last page of the chapter, make the chapter completed call (to account for a marging of error where the last progress update may not have been registered)
    if (Number(pageId) + 1 === totalNumberOfPages - 1 && ['training'].includes(type)) {
      const { email } = user;
      await APIHandler.setChapterCompleted(appId, email, chapterId, chapterNumber);
      const d = new Date(Date.now());
      const leading = (val) => `0${val}`.slice(-2);
      const date = `${d.getFullYear()}-${leading(d.getMonth() + 1)}-${leading(d.getDate())}`;

      await APIHandler.updateUser(appId, email, [
        { param: 'sales_companion_registered', val: date },
        { param: 'training_started', val: date }, // why again?
        // { param: 'training_graduated', val: date }, // why graduate a user here? Shouldn't all chapters be completed?
      ]);
    }

    // Handle further push to history, based on type

    if (['training'].includes(type)) {
      await progressUpdate();
      const { url } = match;
      if (url !== nextPageUrl) {
        if (Number(pageId) + 1 < totalNumberOfPages) {
          history.push(nextPageUrl);
        } else {
          history.push(returnBtnUrl);
        }
      }
    }

    if (['retain', 'fasttrack'].includes(type)) {
      if (Number(pageId) + 1 < totalNumberOfPages) {
        fastTrackData('answered', (+fastTrackData('answered') || 0) + 1);
        history.push(nextPageUrl);
      } else {
        if (type === 'fasttrack') {
          fastTrackData('finished', true);
          fastTrackData('answered', (+fastTrackData('answered') || 0) + 1);
        }
        history.push(`/main/${type}`);
      }
    }
  }, [
    loadedPageChapter,
    completedBefore,
    contentIncomplete,
    type,
    pageChapter,
    pageId,
    chapterPage,
    user,
    appId,
    chapterId,
    progressUpdate,
    match,
    nextPageUrl,
    history,
    returnBtnUrl,
  ]);

  const handlePreviousPage = useCallback(async () => {
    if (!loadedPageChapter) return;
    window.scrollTo(0, 0);

    setCompletedBefore(null);

    if (['training'].includes(type)) {
      if (Number(pageId) > 0) {
        // Still call the progress update, but with the previous page values (to prevent the progress from being marked as completed despite going back)
        await progressUpdate({ previous: true });
        if (match.url !== previousPageUrl) {
          history.push(previousPageUrl);
        }
      } else {
        history.push(returnBtnUrl);
      }
    }

    if (['retain', 'fasttrack'].includes(type)) {
      history.push(previousPageUrl);
    }
  }, [history, loadedPageChapter, match.url, pageId, previousPageUrl, progressUpdate, returnBtnUrl, type]);

  // Alternative navigation button for the final page of the final training content page.
  const navigationButtonText = useMemo(() => {
    // prerequisit data includes the base-prequisites, as well as the pageChapter data for the current page. defaults to general.next
    if (!loadedPrerequisites || !loadedPageChapter) return i18n.translate('general.next');

    const lastChapter = chapters.reduce(
      (prevChapter, currentChapter) => {
        const { chapterNumber: prevChapterNumber } = prevChapter;
        const { chapterNumber: currentChapterNumber } = currentChapter;
        if (Number(currentChapterNumber) > Number(prevChapterNumber)) return currentChapter;
        return prevChapter;
      },
      { chapterNumber: -1 },
    );

    const { chapterItemCount: currentChapterTotalPages } = pageChapter;
    const { id: lastChapterId } = lastChapter;

    const currentPageIsFinalPage = Number(pageId) + 1 === Number(currentChapterTotalPages);
    const currentChapterIsFinalChapter = Number(chapterId) === Number(lastChapterId);

    // If this is the final page of THIS chapter, and/or this chapter is the latest chapter, this is the last page and it should change button text to adjust for this.
    // Adjusted for pageId being 0-based, while chapterItemCount is 1-based
    if (currentPageIsFinalPage && currentChapterIsFinalChapter) return i18n.translate('general.courseComplete');
    if (currentPageIsFinalPage) return i18n.translate('general.chapterComplete');
    return i18n.translate('general.next');
  }, [chapterId, chapters, loadedPageChapter, loadedPrerequisites, pageChapter, pageId]);

  /**
   * Renders the training content based on the current pageData. Taken as a direct copy from original class-based
   * component, with minor adjustments to match new functional-based component structure.
   *
   * There has not been any significant changes to the logic of the component, and as such it may be worth looking at
   * potential improvements at a later date.
   */
  const renderedContent = useMemo(() => {
    if (!chapterPage) return null;

    const totalErrors = Number(fastTrackData('errors')) || 0;
    if (type === 'fasttrack' && totalErrors === -1) {
      history.push('/main/training');
    }

    const { incorrectAnswerText, correctAnswerText } = chapterPage;

    let onError = errors >= 1;

    // function to handle the completion of the content. Moved out of the TrainingContent component declaration for readability
    const handleContentComplete = () => {
      setContentCompleted(true);
      setErrors(0);
    };

    /**
     * Function to handle the increment of errors => Triggered in question components with increment === true on WRONG
     * answer submits. Moved out of the TrainingContent component declaration for readability.
     *
     * Currently handles both the increment of the error count, as well as the configuration of the SweetAlert component
     * (which is then rendered inside of the question component) TODO: Split into two separate functions for better
     * readability and maintainability. ?
     *
     * @param {boolean} increment - Whether or not to increment the error count
     * @returns {JSX.Element} The SweetAlert component
     */
    const handleErrorIncrement = (increment) => {
      if (increment) {
        setErrors((prevState) => prevState + 1);
        setContentCompleted(false);
        if (type === 'fasttrack') {
          fastTrackData('errors', totalErrors + 1);
        }
      }

      let titleCopy =
        sweetAlertTitle ||
        i18n.translate(
          (type === 'fasttrack' && (onError ? incorrectAnswerText : correctAnswerText)) ||
            `trainingScreens.${type === 'fasttrack' ? 'incorrect' : 'trouble'}`,
          {
            errors: 3 - totalErrors,
            mistakes: i18n.translate(`trainingScreens.mistake${3 - totalErrors > 1 ? 's' : ''}`),
          },
        );

      // TODO: Look at more configurable implementation of Error sweetalert
      let iconPopup = '';
      let closeCopy = i18n.translate('trainingScreens.skip');
      if (onError && type === 'fasttrack') {
        iconPopup = 'error';
        closeCopy = i18n.translate('trainingScreens.next');
      } else if (type === 'training' && errors === 1) {
        iconPopup = 'error';
        closeCopy = i18n.translate('general.close');
        titleCopy = incorrectAnswerText;
      } else if (type === 'training' && errors >= 2) {
        iconPopup = '';
        closeCopy = i18n.translate('trainingScreens.skip');
      } else if (type === 'fasttrack') {
        iconPopup = 'success';
        closeCopy = i18n.translate('trainingScreens.next');
      }

      return (
        <SweetAlert
          trigger={
            (type === 'fasttrack' && contentCompleted) || // When type is fasttrack, if and only if the content is completed
            (type === 'training' && errors === 3 && !alternativeTrain) // When type is training, if and only if the questions are not cumulated at the end + error count equals 3
          }
          {...{
            onClose: ({ dismiss, value }) => {
              onError = false;
              if (value === true) {
                if (type !== 'fasttrack' && errors !== 1) {
                  setErrors({ errors: 0 });
                  handlePreviousPage();
                } else if (type === 'fasttrack') {
                  setErrors({ errors: 0 });
                  handleNextPage();
                }
              }
              if (dismiss && onError) setErrors({ errors: 0 });
            },
            onOpen: () => {
              if (!onError) {
                const correctAnswers = fastTrackData('correctAnswers') || [];
                fastTrackData('correctAnswers', [...correctAnswers, pages[pageId] && pages[pageId].UPID]);
              }
            },
            icon: iconPopup,
            allowOutsideClick: false,
            confirmButtonText: closeCopy,
            title: titleCopy,
          }}
        />
      );
    };

    return (
      <TrainingContent
        type={type}
        appId={appId}
        params={pages[pageId]}
        pageId={pageId}
        chapterId={chapterId}
        LinkButtons={buttons}
        contentComplete={handleContentComplete}
        error={handleErrorIncrement}
      />
    );
  }, [
    chapterPage,
    type,
    errors,
    appId,
    pages,
    pageId,
    chapterId,
    buttons,
    history,
    sweetAlertTitle,
    contentCompleted,
    handlePreviousPage,
    handleNextPage,
    alternativeTrain,
  ]);

  return (
    <React.Fragment>
      {/* Progress tracker, displayed on top of screen. */}
      {loadedPrerequisites && loadedPageChapter && (type === 'fasttrack' || isAccessible) && (
        <div className="progress__container" key="progress__container">
          <Progress id="training_progress" value={chapterPercentage} />
        </div>
      )}

      <PoseGroup flipMove={false}>
        {loadedPageChapter && (
          <AnimationContainer className="training" key={pageId}>
            <div className="trainingBackBtn">
              <Link to={returnBtnUrl} className="backBtn">
                <FontAwesomeIcon icon={['fas', 'times']} />
              </Link>
            </div>

            <style>{'\
              #openWidget { display: block; bottom: 4rem; } '}</style>

            <style>
              {`
        :root {
          --section-color: ${color};
        }
      `}
            </style>

            <section className="trainingContent" key="trainingContent">
              {loadedPageChapter && (type === 'fasttrack' || isAccessible) && (
                <div>
                  <style>{'\
                    #openWidget { display: block; bottom: 4rem; } '}</style>

                  <div id="parent" className="trainingContent__content">
                    {renderedContent}
                  </div>

                  {/* Renders training navigation buttons and footer */}
                  <div className="trainingContent__buttons">
                    {type !== 'fasttrack' && (
                      <NavigationButton
                        button={{
                          navigate: i18n.translate(`general.${+pageId === 0 ? 'overview' : 'previous'}`),
                          disabled: type === 'fasttrack',
                          onClick: handlePreviousPage,
                          type: 'previous',
                        }}
                      />
                    )}

                    {+pageId === 0 && type === 'fasttrack' && <span />}

                    <div className="trainingContent__pageNumber">
                      <p>{`${+pageId + 1} / ${chapterItemCount}`}</p>
                    </div>

                    {((+pageId === 0 && type === 'fasttrack') || type !== 'fasttrack') && (
                      <NavigationButton
                        button={{
                          navigate: navigationButtonText,
                          onClick: handleNextPage,
                          type: 'next',
                          id: `nextButton${
                            (!completedBefore && contentIncomplete) || (contentIncomplete && !completedBefore)
                          }`,
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* IF the progressHandler has been loaded, but that the page is not accessible AND the alternative URL is not the current url */}
              {loadedProgressHandler && !isAccessible && match.url !== target && type !== 'fasttrack' && (
                <Redirect push to={target} />
              )}
            </section>
          </AnimationContainer>
        )}
      </PoseGroup>

      <SweetAlert
        trigger={pageNotCompleted}
        {...{
          // TODO: This is a temporary inclusion that allows testers to skip through content. This should be removed in the final version.
          // onClose: () => {
          //   setPageNotCompleted(false);
          //   setContentCompleted(true);
          //   setCompletedBefore(true);
          //   setTimerCompleted(true);
          // },
          onOpen: () => {},
          icon: '',
          title: i18n.translate('trainingScreens.viewAllTitle'),
          text: i18n.translate('trainingScreens.viewAll'),
          confirmButtonText: i18n.translate('general.close'),
        }}
      />
    </React.Fragment>
  );
};

TrainingContentView.propTypes = {
  history: PropTypes.shape().isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape().isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  redirPath: PropTypes.string,
  type: PropTypes.string,
};

export default withRouter(TrainingContentView);
